/**
 * /outbox — pending and recently-sent sales-order submissions.
 *
 * Sources from the Dexie outbox via dexie-react-hooks (useLiveQuery) so the
 * list re-renders automatically when the background processor finishes a
 * send. Buttons:
 *   - Retry now: re-marks an entry pending and triggers a drain immediately
 *   - Discard: removes a queued entry (only legal for failed/pending)
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { RefreshCw } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { ConfirmDialog } from '../components/ui/confirm-dialog.js';
import { Pill, type PillVariant } from '../components/ui/pill.js';
import {
  getOutboxDb,
  listEntries,
  listFieldEvents,
  removeEntry,
  removeFieldEvent,
  retryEntry,
  retryFieldEvent,
  type FieldEventEntry,
  type OutboxStatus,
  type SalesOrderOutboxEntry,
} from '../lib/outbox.js';
import { drainOutbox } from '../lib/outbox-processor.js';

export function OutboxScreen() {
  const entries = useLiveQuery(() => listEntries(getOutboxDb()), []);
  const fieldEvents = useLiveQuery(() => listFieldEvents(getOutboxDb()), []);

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Outbox</h1>
          <p className="text-sm text-ink-muted">
            Quotes and field captures (parts, photos, notes, signatures) queued on this device.
            They submit to Spire automatically when the connection is available.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            void drainOutbox();
          }}
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Sync now
        </Button>
      </header>

      {entries === undefined || fieldEvents === undefined ? (
        <Card>
          <CardBody>
            <div className="text-sm text-ink-muted">Loading…</div>
          </CardBody>
        </Card>
      ) : entries.length === 0 && fieldEvents.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-sm text-ink-muted">Nothing in the outbox.</div>
          </CardBody>
        </Card>
      ) : (
        <>
          {entries.length > 0 && (
            <ul className="space-y-2">
              {entries.map((entry) => (
                <li key={entry.entryId}>
                  <EntryCard entry={entry} />
                </li>
              ))}
            </ul>
          )}

          {fieldEvents.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-medium text-ink pt-2">Field captures</h2>
              <ul className="space-y-2">
                {fieldEvents.map((event) => (
                  <li key={event.entryId}>
                    <FieldEventCard event={event} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

const FIELD_EVENT_LABEL: Record<FieldEventEntry['payload']['kind'], string> = {
  addItem: 'Part',
  addPhoto: 'Photo',
  addNote: 'Note',
  attachSignature: 'Signature',
};

function FieldEventCard({ event }: { event: FieldEventEntry }) {
  const label = FIELD_EVENT_LABEL[event.payload.kind];
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const detail =
    event.payload.kind === 'addItem'
      ? `${event.payload.data.partNo} × ${event.payload.data.orderQty}`
      : event.payload.kind === 'addNote'
        ? `${event.payload.data.body.slice(0, 60)}${event.payload.data.body.length > 60 ? '…' : ''}`
        : null;
  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <StatusPill status={event.status} />
          <CardTitle className="truncate">
            {label} · <span className="font-mono">{event.spireOrderNo}</span>
          </CardTitle>
        </div>
        <div className="text-xs text-ink-muted font-mono shrink-0">{formatAge(event.createdAt)}</div>
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        {detail && <div className="text-xs text-ink">{detail}</div>}
        {event.lastError && event.status !== 'sent' && (
          <div className="text-xs text-red-700">
            <span className="text-ink-muted">Attempt {event.attempts}:</span> {event.lastError}
          </div>
        )}
        {(event.status === 'pending' || event.status === 'failed') && (
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                await retryFieldEvent(event.entryId);
                void drainOutbox();
              }}
            >
              Retry now
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDiscard(true)}
            >
              Discard
            </Button>
          </div>
        )}
        {confirmDiscard && (
          <ConfirmDialog
            title={`Discard this ${label.toLowerCase()}?`}
            message={`It hasn't reached Spire yet. Discarding deletes it from this device and it will never be added to order ${event.spireOrderNo}.`}
            confirmLabel="Discard"
            cancelLabel="Keep"
            onConfirm={() => {
              setConfirmDiscard(false);
              void removeFieldEvent(event.entryId);
            }}
            onCancel={() => setConfirmDiscard(false)}
          />
        )}
      </CardBody>
    </Card>
  );
}

function EntryCard({ entry }: { entry: SalesOrderOutboxEntry }) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const kind = entry.body.udf?.fop_tech ? 'field job' : entry.body.type === 'Q' ? 'quote' : 'order';
  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <StatusPill status={entry.status} />
          <CardTitle className="truncate">
            {entry.body.shippingAddress?.name ?? entry.body.customerNo}
          </CardTitle>
          <span className="text-xs text-ink-muted">
            {kind} · {entry.body.items.length} line
            {entry.body.items.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="text-xs text-ink-muted font-mono shrink-0">{formatAge(entry.createdAt)}</div>
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        <ul className="space-y-0.5 text-xs text-ink">
          {entry.body.items.slice(0, 3).map((line, idx) => (
            <li key={idx}>
              <span className="font-mono">{line.partNo || '(comment)'}</span>{' '}
              <span className="text-ink-muted">× {line.orderQty}</span>
              {line.unitPrice && (
                <span className="text-ink-muted"> @ ${line.unitPrice}</span>
              )}
            </li>
          ))}
          {entry.body.items.length > 3 && (
            <li className="text-ink-muted">+{entry.body.items.length - 3} more…</li>
          )}
        </ul>

        {entry.status === 'sent' && entry.lastOrderNo && entry.lastSpireId !== null && (
          <div className="text-xs text-emerald-700">
            Submitted as{' '}
            <Link
              to="/sales-orders/$id"
              params={{ id: String(entry.lastSpireId) }}
              className="font-mono text-ink-accent hover:underline"
            >
              {entry.lastOrderNo}
            </Link>
          </div>
        )}

        {entry.lastError && entry.status !== 'sent' && (
          <div className="text-xs text-red-700">
            <span className="text-ink-muted">Attempt {entry.attempts}:</span> {entry.lastError}
          </div>
        )}

        {(entry.status === 'pending' || entry.status === 'failed') && (
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                await retryEntry(entry.entryId);
                void drainOutbox();
              }}
            >
              Retry now
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDiscard(true)}
            >
              Discard
            </Button>
          </div>
        )}
        {confirmDiscard && (
          <ConfirmDialog
            title={`Discard this ${kind}?`}
            message={`It hasn't reached Spire yet. Discarding deletes it from this device for good, including any signature, photos and notes attached to it.`}
            confirmLabel="Discard"
            cancelLabel="Keep"
            onConfirm={() => {
              setConfirmDiscard(false);
              void removeEntry(entry.entryId);
            }}
            onCancel={() => setConfirmDiscard(false)}
          />
        )}
      </CardBody>
    </Card>
  );
}

function StatusPill({ status }: { status: OutboxStatus }) {
  const map: Record<OutboxStatus, { variant: PillVariant; label: string; dot?: boolean }> = {
    pending: { variant: 'warning', label: 'Pending', dot: true },
    sending: { variant: 'info', label: 'Sending', dot: true },
    failed: { variant: 'danger', label: 'Failed' },
    sent: { variant: 'success', label: 'Sent' },
  };
  const cfg = map[status];
  return (
    <Pill variant={cfg.variant} dot={cfg.dot}>
      {cfg.label}
    </Pill>
  );
}

function formatAge(timestamp: number): string {
  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}
